/**
 * The grant dialog's bucket-provisioning **option set** and the four states its
 * catalogue request can be in (provision-sub-account-from-grant-dialog, D3/D4).
 *
 * A pure module on purpose (design D8): the console has no component-rendering
 * harness, so every rule that could be got wrong — which types are offered,
 * which of the two empty states applies, when the provisioning control may be
 * activated — lives here and is unit-tested directly.
 *
 * Two rules carry the weight:
 *
 * 1. **Held types are subtracted.** `POST /accounts/<id>/sub-accounts` is
 *    get-or-create at the sub-account level too, so submitting a `type_key` the
 *    account already holds returns that existing — possibly `frozen` or
 *    `closed` — bucket with a `200`, and the dialog would report a provisioning
 *    that changed nothing.
 * 2. **A failed catalogue is never an empty catalogue.** Only a `2xx` carrying
 *    no entry means "this application has no bucket type".
 *
 * grant-dialog-monetary-bonus-target (design D8): the offered set is no longer
 * filtered to `virtual`. The step is reachable in two modes — `any` (the dialog
 * opened on an account with no `active` bucket at all) and `monetary` (the
 * grant form's entry point for an account that holds a virtual bucket but no
 * monetary one) — and the two empty states stay distinguishable in both: "this
 * application has no bucket type" and "this application has no MONETARY bucket
 * type" are different facts with the same next step, and are said in their own
 * words rather than collapsed.
 */

/** One entry of `GET /api/v1/accounts/sub-account-types`, of any asset kind. */
export interface SubAccountTypeRow {
  type_key: string
  display_name: string
  asset_kind: string
  currency: string | null
  app_creditable: boolean
  spending_priority: number
  withdrawable: boolean
}

/** The minimum an already-held bucket must expose to be subtracted. */
export interface HeldSubAccount {
  type: string
}

/** Which asset kinds the step is offering (design D8). */
export type ProvisionMode = 'any' | 'monetary'

export interface ProvisionOption {
  typeKey: string
  displayName: string
  appCreditable: boolean
  /** The type config's own asset kind — sent verbatim in the request body. */
  assetKind: string
  /** The type config's own currency: an ISO code for monetary, else `null`. */
  currency: string | null
  /** Displayed so the operator sees the spend order; never sent. */
  spendingPriority: number
  /**
   * True exactly when `appCreditable` is false: such a bucket can be granted
   * into by an admin but never clawed back by the reversal action, and
   * `app_creditable` is immutable once the type config exists — so the operator
   * must acknowledge that before the provisioning can be submitted.
   */
  acknowledgementRequired: boolean
}

/**
 * The tenant's bucket types — of every asset kind, or of `monetary` alone in
 * `monetary` mode — minus every type the account already holds, **in any
 * status**. Order is the server's (spending_priority, then type_key).
 *
 * Returns a new array; neither input is mutated.
 */
export function buildProvisionOptions(
  types: readonly SubAccountTypeRow[],
  subAccounts: readonly HeldSubAccount[],
  mode: ProvisionMode = 'any'
): readonly ProvisionOption[] {
  const held = new Set(subAccounts.map((bucket) => bucket.type))
  return types
    .filter((type) => mode !== 'monetary' || type.asset_kind === 'monetary')
    .filter((type) => !held.has(type.type_key))
    .map((type) => ({
      typeKey: type.type_key,
      displayName: type.display_name,
      appCreditable: type.app_creditable === true,
      assetKind: type.asset_kind,
      currency: type.currency,
      spendingPriority: type.spending_priority,
      acknowledgementRequired: type.app_creditable !== true,
    }))
}

/** The catalogue request's own state — a request, never a given. */
export type CatalogueState =
  | { status: 'loading' }
  | { status: 'failed'; message: string }
  | { status: 'loaded'; types: readonly SubAccountTypeRow[] }

/**
 * What the provisioning step renders. The two empty states are distinct
 * because their causes and their next steps are: `no-types` means the
 * application has no virtual bucket type configured at all (create one through
 * the provisioning endpoint with a new `type_key`), `all-held` means the
 * account already holds a bucket of every configured type and none is active
 * (reactivation is out of this change's scope).
 */
export type ProvisionStep =
  | { kind: 'loading' }
  | { kind: 'failed'; message: string }
  | { kind: 'no-types' }
  | { kind: 'no-monetary-types' }
  | { kind: 'all-held' }
  | { kind: 'options'; options: readonly ProvisionOption[] }

export function resolveProvisionStep(
  catalogue: CatalogueState,
  options: readonly ProvisionOption[],
  mode: ProvisionMode = 'any'
): ProvisionStep {
  // A failed listing SHALL NEVER resolve to `no-types`.
  if (catalogue.status === 'loading') return { kind: 'loading' }
  if (catalogue.status === 'failed') return { kind: 'failed', message: catalogue.message }
  if (catalogue.types.length === 0) return { kind: 'no-types' }
  // "No monetary type configured" is its own sentence: the application HAS
  // types, so claiming it has none would send the operator after the wrong
  // thing. Same endpoint to fix it, different fact.
  if (
    mode === 'monetary' &&
    !catalogue.types.some((type) => type.asset_kind === 'monetary')
  ) {
    return { kind: 'no-monetary-types' }
  }
  if (options.length === 0) return { kind: 'all-held' }
  return { kind: 'options', options }
}

/** Everything the "should I ask the server for the catalogue?" decision reads. */
export interface CatalogueRequestGate {
  open: boolean
  /** The live dialog session's id, or `null` while there is none. */
  sessionId: number | null
  /** The session a catalogue request was already issued for, or `null`. */
  requestedForSession: number | null
}

/**
 * Whether the catalogue must be requested now.
 *
 * round-2 fix (F1, category race-condition). The marker for "already asked" is
 * the **session id**, never a bare boolean. A session can die before its answer
 * arrives (StrictMode's mount/cleanup/mount replay, a close, a navigation), and
 * an answer of a dead session is discarded by `sendInSession` — so a boolean
 * marker leaves the step waiting forever on a request whose reply can no longer
 * be applied. Keyed by session, a new session means a new request; the same
 * session is never asked twice.
 */
export function shouldRequestCatalogue(gate: CatalogueRequestGate): boolean {
  // grant-dialog-monetary-bonus-target (design D2): the catalogue is requested
  // whenever the dialog OPENS, not only when it shows the provisioning step —
  // the grant form reads its targets' `spending_priority` and `withdrawable`
  // from it, and the default preselection is a function of those priorities.
  if (!gate.open) return false
  if (gate.sessionId === null) return false
  return gate.requestedForSession !== gate.sessionId
}

/** The provisioning submission phases that forbid a (further) submission. */
export type ProvisionPhase =
  | 'idle'
  | 'submitting'
  | 'confirming'
  | 'failed'
  | 'unconfirmed'
  | 'confirmed'

export interface ProvisionSubmitGate {
  step: ProvisionStep
  /** The provisioning step's own submission status (see `provisionSession`). */
  phase: ProvisionPhase
}

/**
 * Whether the provisioning control may be activated.
 *
 * False while the catalogue is loading or failed, false in either empty state,
 * false without a selection or with a selection that is not on offer, false
 * when the selected type needs an acknowledgement that was not given, and false
 * while a provisioning request or its confirming reload is in flight.
 */
export function canSubmitProvision(
  selection: string | null,
  acknowledged: boolean,
  gate: ProvisionSubmitGate
): boolean {
  if (gate.step.kind !== 'options') return false
  // Only the two resting phases may submit: `submitting` and `confirming` are
  // in flight, `unconfirmed`/`confirmed` are past the provisioning itself.
  if (gate.phase !== 'idle' && gate.phase !== 'failed') return false
  if (!selection) return false
  const option = gate.step.options.find((candidate) => candidate.typeKey === selection)
  if (!option) return false
  if (option.acknowledgementRequired && !acknowledged) return false
  return true
}
